import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  ButtonGroup,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import {
  getFlipkartOrderList,
  deleteFlipkartOrder,
} from "app/redux/slice/flipkartSlice";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { userType } from "app/helpers/userType";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const statusOptions = [
  { value: "DELIVERED", label: "Delivered", color: "success" },
  { value: "RETURNED", label: "Returned", color: "error" },
  { value: "RETURN_REQUESTED", label: "Return Requested", color: "warning" },
  { value: "RETURN_CANCELLED", label: "Return Cancelled", color: "info" },
  { value: "IN_TRANSIT", label: "In Transit", color: "primary" },
  { value: "CANCELLED", label: "Cancelled", color: "default" },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount || 0);

const EXCLUDED_KEYS = ["_id", "__v", "createdAt", "updatedAt", "deletedAt"];

const formatFieldLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return moment.utc(value).format("DD/MM/YYYY");
  }
  if (Array.isArray(value)) {
    return value.length ? JSON.stringify(value, null, 2) : "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const formatReadable = (value) =>
  value
    ? value
        .toString()
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "-";

const FlipkartOrdersList = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const flipkartData = useSelector((state) => state?.flipkart);
  const orders = flipkartData?.orders?.list ?? [];

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ orderStatus: "" });
  const [dateRange, setDateRange] = useState([null, null]);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilters, setTempFilters] = useState({
    orderStatus: "",
  });
  const [tempDateRange, setTempDateRange] = useState([null, null]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("orderDate");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "flipkart-orders-list" });

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, filters, dateRange, order, orderBy]);

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    deletePermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        deletePermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "flipkart-order-delete") {
            permissionValue.deletePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  const fetchOrders = () => {
    const params = {
      page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
    };

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });

    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getFlipkartOrderList(params));
  };

  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setFilters({ ...tempFilters });
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    const cleared = { orderStatus: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    handleChangePage(0, 0);
  };

  const getStatusChip = (status) => {
    const cfg = statusOptions.find((o) => o.value === status);
    return (
      <Chip
        label={cfg?.label || formatReadable(status)}
        color={cfg?.color || "default"}
        size="small"
      />
    );
  };

  const handleDeleteOrder = () => {
    dispatch(deleteFlipkartOrder(deleteActionProps))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "success" });
          fetchOrders();
          closeDelete();
        } else {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "error" });
        }
      })
      .catch(() => {});
  };

  const columns = [
    { id: "orderId", label: "Order ID", sort: false },
    { id: "orderItemId", label: "Order Item ID", sort: false },
    {
      id: "orderDate",
      label: "Order Date",
      sort: false,
      render: (row) => (
        <>
          {row?.orderDate
            ? moment.utc(row.orderDate).format("DD/MM/YYYY")
            : "-"}
        </>
      ),
    },
    {
      id: "skuName",
      label: "SKU",
      sort: false,
      render: (row) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {row?.skuName || "-"}
        </Typography>
      ),
    },
    {
      id: "orderStatus",
      label: "Status",
      sort: false,
      render: (row) => getStatusChip(row?.orderStatus),
    },
    {
      id: "saleAmount",
      label: "Sale Amount",
      sort: false,
      format: (v) => formatCurrency(v),
    },
    {
      id: "netEarnings",
      label: "Net Earnings",
      sort: false,
      format: (v) => formatCurrency(v),
    },
    {
      id: "amountSettled",
      label: "Amount Settled",
      sort: false,
      format: (v) => formatCurrency(v),
    },
    {
      id: "amountPending",
      label: "Amount Pending",
      sort: false,
      format: (v) => formatCurrency(v),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedOrder(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Flipkart Orders List
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by Order ID, Item ID, SKU..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={setTempDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ButtonGroup size="small" fullWidth variant="outlined">
                {QUICK_RANGES.map((r) => (
                  <Button
                    key={r.key}
                    onClick={() => setTempDateRange(r.getRange())}
                  >
                    {r.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={tempFilters.orderStatus}
                  onChange={(e) =>
                    setTempFilters((p) => ({
                      ...p,
                      orderStatus: e.target.value,
                    }))
                  }
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {statusOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  fullWidth
                  size="small"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  size="small"
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {flipkartData?.orders?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={orders}
              page={page}
              rowsPerPage={rowsPerPage}
              count={flipkartData?.orders?.total || orders.length}
              Order={order}
              OrderBy={orderBy}
              sortData={(o, ob) => {
                setOrder(o);
                setOrderBy(ob);
              }}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={flipkartData?.orders?.loading}
              showTotalInFooter={false}
            />
          </CardContent>
        </Card>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedOrder?._id) {
              setOrderDetails(selectedOrder);
              setDetailsDialog(true);
            }
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            deleteStageAction(selectedOrder?._id);
          }}
          disabled={!permission.deletePermission}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent dividers>
          {orderDetails && (
            <Grid container spacing={2}>
              {Object.entries(orderDetails)
                .filter(([key]) => !EXCLUDED_KEYS.includes(key))
                .map(([key, value], i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2">
                        {formatFieldLabel(key)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {formatFieldValue(key, value)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DialogConfirm
        isOpen={isDelete}
        onClose={closeDelete}
        title="Are you sure you want to delete this order?"
        onConfirm={handleDeleteOrder}
        btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
      />
    </Box>
  );
};

export default FlipkartOrdersList;
